import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { Counter, Page, Petals, Reveal, Select } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';

const WORDS = ['forever', 'partner', 'soulmate', 'home'];

function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="rotator">
      <AnimatePresence mode="wait">
        <motion.em
          key={WORDS[i]}
          initial={{ y: '60%', opacity: 0, filter: 'blur(6px)' }}
          animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
          exit={{ y: '-60%', opacity: 0, filter: 'blur(6px)' }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          {WORDS[i]}.
        </motion.em>
      </AnimatePresence>
    </span>
  );
}

function QuickSearch() {
  const { meta } = useApp();
  const navigate = useNavigate();
  const [f, setF] = useState({ lookingFor: 'Bride', ageMin: '24', ageMax: '30', religion: '', motherTongue: '' });
  const ages = Array.from({ length: 33 }, (_, i) => String(21 + i));
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <motion.form
      className="qsearch"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55, duration: 0.6 }}
      onSubmit={(e) => {
        e.preventDefault();
        navigate('/register', { state: { gender: f.lookingFor === 'Bride' ? 'Male' : 'Female', search: f } });
      }}
    >
      <div className="qsearch__seg">
        {['Bride', 'Groom'].map((g) => (
          <button type="button" key={g} className={f.lookingFor === g ? 'is-active' : ''} onClick={() => set('lookingFor')(g)}>
            {f.lookingFor === g && <motion.span layoutId="qs-pill" className="qsearch__pill" />}
            <span>I'm looking for a {g}</span>
          </button>
        ))}
      </div>
      <div className="qsearch__row">
        <label>
          <small>Age</small>
          <div className="qsearch__age">
            <Select value={f.ageMin} onChange={set('ageMin')} options={ages} placeholder="From" />
            <span>to</span>
            <Select value={f.ageMax} onChange={set('ageMax')} options={ages} placeholder="To" />
          </div>
        </label>
        <label>
          <small>Religion</small>
          <Select value={f.religion} onChange={set('religion')} options={meta?.options.RELIGIONS || []} placeholder="Any" />
        </label>
        <label>
          <small>Mother tongue</small>
          <Select value={f.motherTongue} onChange={set('motherTongue')} options={meta?.options.MOTHER_TONGUES || []} placeholder="Any" />
        </label>
        <motion.button className="btn btn--primary qsearch__go" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
          Let's begin <Icon name="arrowRight" size={18} />
        </motion.button>
      </div>
    </motion.form>
  );
}

function HeroCollage({ people }) {
  const a = people[0];
  const b = people.find((p) => p.gender !== a?.gender) || people[1];
  const c = people[2];
  const card = (p, cls, delay) =>
    p && (
      <motion.div
        className={`collage__card ${cls}`}
        initial={{ opacity: 0, y: 40, rotate: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5 + delay * 2, repeat: Infinity, ease: 'easeInOut' }}>
          <img src={p.photo} alt={p.firstName} />
          <div className="collage__meta">
            <strong>{p.firstName}, {p.age}</strong>
            <small>{p.specialty} · {p.city}</small>
          </div>
        </motion.div>
      </motion.div>
    );
  return (
    <div className="collage">
      <div className="collage__glow" />
      {card(a, 'collage__card--a', 0.3)}
      {card(b, 'collage__card--b', 0.5)}
      {card(c, 'collage__card--c', 0.7)}
      <motion.div className="collage__bubble collage__bubble--match" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.3, type: 'spring' }}>
        <motion.span animate={{ scale: [1, 1.25, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}>
          <Icon name="heart" size={18} fill="currentColor" stroke={0} />
        </motion.span>
        It's a match!
      </motion.div>
      <motion.div className="collage__bubble collage__bubble--kundli" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.6, type: 'spring' }}>
        <Icon name="moon" size={16} /> 31 / 36 gunas
      </motion.div>
      <motion.div className="collage__bubble collage__bubble--verified" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.9, type: 'spring' }}>
        <Icon name="shield" size={16} /> Verified doctor
      </motion.div>
    </div>
  );
}

const STEPS = [
  { icon: 'steth', title: 'Sign in with MedHire', text: 'Already on MedHire? Your education, work history and personal details fill in automatically. Or register in 3 minutes.' },
  { icon: 'sparkle', title: 'Meet curated matches', text: 'Daily picks scored on your preferences, profession, city and horoscope — not endless scrolling.' },
  { icon: 'chat', title: 'Connect & chat', text: 'Send interests, chat once accepted, check Kundli compatibility and involve family at your pace.' },
];

const FEATURES = [
  { icon: 'shield', tint: 'teal', title: 'Verified healthcare profiles', text: 'MedHire-linked and manually screened profiles you can trust.' },
  { icon: 'moon', tint: 'violet', title: 'Kundli & Guna Milan', text: 'Instant Ashtakoot compatibility with every profile you view.' },
  { icon: 'lock', tint: 'rose', title: 'You control privacy', text: 'Hide photos, salary or contact details — reveal only when you are ready.' },
  { icon: 'family', tint: 'gold', title: 'Family friendly', text: 'Profiles can be created and managed by parents and siblings too.' },
  { icon: 'calendar', tint: 'teal', title: 'Built around shifts', text: 'Night duty? Chats wait for you, and matches understand the hours.' },
  { icon: 'crown', tint: 'gold', title: 'Premium advisors', text: 'Relationship managers who know the medical community personally.' },
];

export default function Landing() {
  const [people, setPeople] = useState([]);
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);
  const { meta } = useApp();
  const { scrollY } = useScroll();
  const blobY = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    api('/public/featured').then((d) => setPeople(d.items)).catch(() => {});
    api('/public/stats').then(setStats).catch(() => {});
    api('/public/stories').then((d) => setStories(d.items)).catch(() => {});
  }, []);

  return (
    <Page className="landing">
      <section className="hero">
        <motion.div className="hero__blob hero__blob--1" style={{ y: blobY }} />
        <motion.div className="hero__blob hero__blob--2" style={{ y: blobY }} />
        <Petals />
        <div className="container hero__grid">
          <div className="hero__copy">
            <motion.span className="eyebrow eyebrow--pill" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Icon name="steth" size={15} /> Matrimony for doctors & healthcare heroes
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}>
              Where healers
              <br />
              find their <RotatingWord />
            </motion.h1>
            <motion.p className="hero__sub" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }}>
              CTshaadi brings together doctors, nurses, pharmacists and every healthcare professional who understands that love sometimes runs on night shifts.
            </motion.p>
            <QuickSearch />
            <motion.div className="hero__medhire" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
              <span className="medhire-chip"><Icon name="steth" size={14} /> MedHire</span>
              Have a MedHire account? <Link to="/register?medhire=1">Import your profile in one tap →</Link>
            </motion.div>
          </div>
          <HeroCollage people={people} />
        </div>
      </section>

      <section className="stats container">
        {[
          { n: Math.max(stats?.members || 0, 25000), suffix: '+', label: 'Healthcare members' },
          { n: Math.max(stats?.matches || 0, 3200), suffix: '+', label: 'Happy matches' },
          { n: Math.max(stats?.verified || 0, 18000), suffix: '+', label: 'Verified profiles' },
          { n: 120, suffix: '+', label: 'Cities across India' },
        ].map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="stat">
            <strong><Counter to={s.n} suffix={s.suffix} /></strong>
            <span>{s.label}</span>
          </Reveal>
        ))}
      </section>

      <section className="section container">
        <Reveal className="section__head">
          <span className="eyebrow">How it works</span>
          <h2>Three gentle steps to <em>your person</em></h2>
        </Reveal>
        <div className="steps">
          <motion.svg className="steps__line" viewBox="0 0 1000 40" preserveAspectRatio="none" aria-hidden="true">
            <motion.path d="M20 20 C 250 -10, 350 50, 500 20 S 760 -10, 980 20" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: 'easeInOut' }} />
          </motion.svg>
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={0.2 + i * 0.18} className="step">
              <motion.div className="step__icon" whileHover={{ rotate: 8, scale: 1.08 }}>
                <Icon name={s.icon} size={28} />
                <span className="step__num">{i + 1}</span>
              </motion.div>
              <h3>{s.title}</h3>
              <p className="muted">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="medhire-band">
        <div className="container medhire-band__inner">
          <Reveal>
            <span className="eyebrow eyebrow--light">For MedHire members</span>
            <h2>Your profile is already 80% done.</h2>
            <p>Sign in with your MedHire account and we'll bring over your education, specialty, experience and personal details. You decide what to share — including whether to show the same salary as MedHire or a different one.</p>
            <Link to="/register?medhire=1" className="btn btn--white">
              <Icon name="steth" size={18} /> Continue with MedHire
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="import-demo">
            {['Name & date of birth', 'Specialty & qualification', 'Work history & experience', 'City & languages', 'Salary (your choice to show)'].map((t, i) => (
              <motion.div
                key={t}
                className="import-demo__row"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.15 }}
              >
                <motion.span className="import-demo__tick" initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 + i * 0.15, type: 'spring' }}>
                  <Icon name="check" size={14} stroke={3} />
                </motion.span>
                {t}
              </motion.div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section container">
        <Reveal className="section__head">
          <span className="eyebrow">Why CTshaadi</span>
          <h2>Made for the way <em>you live</em></h2>
        </Reveal>
        <div className="features">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 0.1} className="feature">
              <motion.div whileHover={{ y: -6 }} className="feature__inner">
                <span className={`feature__icon tint-${f.tint}`}><Icon name={f.icon} size={24} /></span>
                <h3>{f.title}</h3>
                <p className="muted">{f.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {people.length > 0 && (
        <section className="section section--tint">
          <Reveal className="section__head container">
            <span className="eyebrow">New on CTshaadi</span>
            <h2>Faces you could <em>meet today</em></h2>
          </Reveal>
          <div className="marquee">
            <div className="marquee__track">
              {[...people, ...people].map((p, i) => (
                <Link to="/register" key={`${p.profileId}-${i}`} className="mcard">
                  <img src={p.photo} alt={p.firstName} loading="lazy" />
                  <div>
                    <strong>{p.firstName}, {p.age}</strong>
                    <small>{p.specialty} · {p.city}</small>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {stories.length > 0 && (
        <section className="section container">
          <Reveal className="section__head">
            <span className="eyebrow">Success stories</span>
            <h2>Matched on shifts, <em>married for life</em></h2>
          </Reveal>
          <div className="stories-row">
            {stories.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12} className="story">
                <div className="story__img"><img src={s.image} alt={s.couple} loading="lazy" /></div>
                <div className="story__body">
                  <p>“{s.quote}”</p>
                  <strong>{s.couple}</strong>
                  <small className="muted">{s.meta}</small>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="center"><Link to="/stories" className="btn btn--ghost">Read more stories <Icon name="arrowRight" size={16} /></Link></div>
        </section>
      )}

      <section className="section container">
        <Reveal className="section__head">
          <span className="eyebrow">Membership</span>
          <h2>Start free. <em>Upgrade</em> when you're ready.</h2>
        </Reveal>
        <div className="plans plans--mini">
          {(meta?.plans || []).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className={`plan ${p.popular ? 'plan--popular' : ''}`}>
              {p.popular && <span className="plan__ribbon">Most loved</span>}
              <h3 style={{ color: p.color }}>{p.name}</h3>
              <div className="plan__price">₹{p.price.toLocaleString('en-IN')}<small> / {p.months} months</small></div>
              <ul>{p.features.slice(0, 3).map((f) => <li key={f}><Icon name="check" size={15} stroke={2.4} />{f}</li>)}</ul>
            </Reveal>
          ))}
        </div>
        <div className="center"><Link to="/premium" className="btn btn--ghost">Compare all plans <Icon name="arrowRight" size={16} /></Link></div>
      </section>

      <section className="cta container">
        <Reveal className="cta__card">
          <Petals count={8} />
          <h2>Your story could be next.</h2>
          <p>Join thousands of healthcare professionals who found their partner on CTshaadi.</p>
          <div className="cta__btns">
            <Link to="/register" className="btn btn--white">Register free</Link>
            <Link to="/register?medhire=1" className="btn btn--outline-light"><Icon name="steth" size={17} /> Continue with MedHire</Link>
          </div>
        </Reveal>
      </section>
    </Page>
  );
}
